import { useCallback, useEffect, useRef, useState } from 'react';
import { MainLayout } from '@/components/layout/MainLayout';
import apiClient from '@/lib/api/client';

// ── Email manager (#49) ─────────────────────────────────────────────────────
// Account rail → contact list → conversation. Mail syncs from the ochrepoint.ca
// Maildir every 3 minutes; compose sends through the branded identity template
// and the sent copy joins the thread.

interface AccountRow { account: string; total: number; unseen: number }
interface Contact {
  id: number; email: string; name: string; msg_count: number; unseen: number;
  latest: string; last_snippet: string; last_subject: string;
  client_id?: number | null; client_name?: string | null;
}
interface Msg {
  id: number; account: string; direction: 'in' | 'out'; from_addr: string; from_name: string;
  to_addrs: string; subject: string; snippet: string; attachments_json: string | null;
  sent_at: string; seen: number;
  lead_score?: number | null; lead_reason?: string | null;
}
interface Sender { key: string; email: string; name: string }
interface MailTemplate { id: string; title: string; category: string; subject: string; body: string }
// One row per (contact, receiving address) from /mail/contacts-tree (#81)
interface TreeRow {
  id: number; email: string; name: string; account: string;
  msg_count: number; unseen: number; latest: string;
}
// One row per unlinked frequent correspondent from /mail/review-contacts (#107)
interface ReviewRow { id: number; email: string; name: string; msg_count: number; latest: string }
interface ClientOpt { id: number; name: string; email?: string | null }
// One row per stored attachment from /mail/attachments (#85)
interface AttRow {
  message_id: number; idx: number; name: string; size: number;
  account: string; direction: 'in' | 'out'; subject: string; sent_at: string;
  contact_id: number; contact_email: string; contact_name: string;
}

const FALLBACK_SENDERS: Sender[] = ['info', 'admin', 'emartin', 'billing', 'support', 'noreply']
  .map(k => ({ key: k, email: `${k}@ochrepoint.ca`, name: k }));

const fmtSize = (b: number) => b < 1024 ? `${b} B` : b < 1048576 ? `${(b / 1024).toFixed(0)} KB` : `${(b / 1048576).toFixed(1)} MB`;

// Level 1 of the drill-down: your email addresses, then the utility folders
const ACCOUNTS: { key: string; label: string; icon: string; addr?: boolean }[] = [
  { key: 'info', label: 'info@ochrepoint.ca', icon: 'alternate_email', addr: true },
  { key: 'admin', label: 'admin@ochrepoint.ca', icon: 'alternate_email', addr: true },
  { key: 'emartin', label: 'emartin@ochrepoint.ca', icon: 'alternate_email', addr: true },
  { key: 'support', label: 'support@ochrepoint.ca', icon: 'alternate_email', addr: true },
  { key: 'sent', label: 'Sent', icon: 'send' },
  { key: 'junk', label: 'Junk', icon: 'report' },
  { key: 'all', label: 'All mail', icon: 'all_inbox' },
];

const fmtTime = (d: string) => {
  const dt = new Date(d);
  const today = new Date().toDateString() === dt.toDateString();
  return today
    ? dt.toLocaleTimeString('en-CA', { hour: 'numeric', minute: '2-digit' })
    : dt.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });
};

export const MailPage = () => {
  const [accounts, setAccounts] = useState<AccountRow[]>([]);
  const [account, setAccount] = useState('info');
  // mobile drill-down: 0 = addresses, 1 = senders, 2 = messages
  const [level, setLevel] = useState(1);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [search, setSearch] = useState('');
  const [active, setActive] = useState<Contact | null>(null);
  const [msgs, setMsgs] = useState<Msg[]>([]);
  const [openBody, setOpenBody] = useState<Record<number, string>>({});
  const [expanded, setExpanded] = useState<number | null>(null);
  const [composeOpen, setComposeOpen] = useState(false);
  const [cTo, setCTo] = useState('');
  const [cSubject, setCSubject] = useState('');
  const [cBody, setCBody] = useState('');
  const [cFrom, setCFrom] = useState('info');
  const [sending, setSending] = useState(false);
  const [notice, setNotice] = useState('');
  const [cFiles, setCFiles] = useState<File[]>([]);
  const [drafting, setDrafting] = useState(false);
  // Optional steering for the reply-box / per-message AI draft
  const [aiInstructions, setAiInstructions] = useState('');
  // Compose-modal AI draft (/mail/ai-compose — no contact required)
  const [cAiInstructions, setCAiInstructions] = useState('');
  const [cDrafting, setCDrafting] = useState(false);
  // cBody/cSubject are shared with the reply box — an ai-compose response that
  // lands after the modal closed must not clobber a reply in progress
  const composeOpenRef = useRef(false);
  useEffect(() => { composeOpenRef.current = composeOpen; }, [composeOpen]);
  // last AI-set subject: overwrite it on re-draft, preserve user-typed ones
  const aiSubjectRef = useRef('');
  // #57 — collapsible per-address contact tree in the rail
  const [treeOpen, setTreeOpen] = useState<Set<string>>(new Set());
  const [treeContacts, setTreeContacts] = useState<Record<string, Contact[]>>({});
  // #80 — richer compose: senders from the API, Cc, saved templates, live preview
  const [senders, setSenders] = useState<Sender[]>(FALLBACK_SENDERS);
  const [cCc, setCCc] = useState('');
  const [templates, setTemplates] = useState<MailTemplate[]>([]);
  const [templateId, setTemplateId] = useState('');
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);
  // #81 — contact-first tree root in the rail (contact → address → thread)
  const [contactsRootOpen, setContactsRootOpen] = useState(false);
  const [contactTree, setContactTree] = useState<TreeRow[] | null>(null);
  const [openContactNodes, setOpenContactNodes] = useState<Set<number>>(new Set());
  // #85 — attachments browser (replaces the list+thread panels while active)
  const [view, setView] = useState<'mail' | 'attachments' | 'review'>('mail');
  const [attRows, setAttRows] = useState<AttRow[] | null>(null);
  const [openAttNodes, setOpenAttNodes] = useState<Set<number>>(new Set());
  // #107 — "Review contacts" sweep: frequent correspondents with no client link
  const [reviewRows, setReviewRows] = useState<ReviewRow[] | null>(null);
  const [clientOpts, setClientOpts] = useState<ClientOpt[] | null>(null);
  const [linkingId, setLinkingId] = useState<number | null>(null);
  const [linkClientId, setLinkClientId] = useState('');
  const [reviewBusy, setReviewBusy] = useState<number | null>(null);
  const [reviewNotice, setReviewNotice] = useState('');

  const toggleTree = async (key: string) => {
    setTreeOpen(o => {
      const n = new Set(o);
      if (n.has(key)) n.delete(key); else n.add(key);
      return n;
    });
    if (!treeContacts[key]) {
      const r = await apiClient.get(`/mail/contacts?account=${key}`).catch(() => null);
      setTreeContacts(prev => ({ ...prev, [key]: r?.data.data ?? [] }));
    }
  };
  const threadEnd = useRef<HTMLDivElement>(null);

  const loadAccounts = useCallback(() =>
    apiClient.get('/mail/overview').then(r => setAccounts(r.data.data ?? [])).catch(() => {}), []);
  const loadContacts = useCallback(() =>
    apiClient.get(`/mail/contacts?account=${account}&search=${encodeURIComponent(search)}`)
      .then(r => setContacts(r.data.data ?? [])).catch(() => {}), [account, search]);

  useEffect(() => { loadAccounts(); }, [loadAccounts]);
  useEffect(() => {
    const t = setTimeout(loadContacts, search ? 300 : 0);
    return () => clearTimeout(t);
  }, [loadContacts, search]);
  useEffect(() => {
    apiClient.get('/mail/senders').then(r => {
      const list = r.data.data as Sender[];
      if (list?.length) setSenders(list);
    }).catch(() => {});
  }, []);

  const loadContactTree = () =>
    apiClient.get('/mail/contacts-tree').then(r => setContactTree(r.data.data ?? [])).catch(() => setContactTree([]));
  const loadAttachments = () =>
    apiClient.get('/mail/attachments').then(r => setAttRows(r.data.data ?? [])).catch(() => setAttRows([]));

  // #107 — review-contacts sweep loaders + row actions
  const loadReview = () =>
    apiClient.get('/mail/review-contacts').then(r => setReviewRows(r.data.data ?? [])).catch(() => setReviewRows([]));
  const loadClientOpts = () =>
    apiClient.get('/clients').then(r => setClientOpts(r.data.data ?? [])).catch(() => setClientOpts([]));

  const reviewMakeClient = async (row: ReviewRow) => {
    if (!window.confirm(`Create a client record for ${row.name || row.email}?`)) return;
    setReviewBusy(row.id);
    try {
      await apiClient.post(`/mail/contacts/${row.id}/create-client`);
      setReviewRows(rows => (rows ?? []).filter(r => r.id !== row.id));
      setReviewNotice(`Client created for ${row.name || row.email} ✓`);
      loadContacts();
    } catch { setReviewNotice('Could not create the client.'); }
    finally { setReviewBusy(null); }
  };

  const reviewDismiss = async (row: ReviewRow) => {
    setReviewBusy(row.id);
    try {
      await apiClient.post(`/mail/review-contacts/${row.id}/dismiss`);
      setReviewRows(rows => (rows ?? []).filter(r => r.id !== row.id));
      setReviewNotice(`${row.email} dismissed — it won't come back.`);
    } catch { setReviewNotice('Could not dismiss.'); }
    finally { setReviewBusy(null); }
  };

  const reviewLink = async (row: ReviewRow) => {
    if (!linkClientId) return;
    setReviewBusy(row.id);
    try {
      const r = await apiClient.post(`/mail/contacts/${row.id}/link-existing`, { client_id: Number(linkClientId) });
      const action = r.data.data?.email_action as string;
      const cl = clientOpts?.find(c => String(c.id) === linkClientId);
      setReviewRows(rows => (rows ?? []).filter(x => x.id !== row.id));
      setReviewNotice(
        action === 'filled' ? `Linked to ${cl?.name || 'client'} — their empty email is now ${row.email}.`
        : action === 'noted' ? `Linked to ${cl?.name || 'client'} — they already have a different email, so ${row.email} was added to their notes.`
        : `Linked to ${cl?.name || 'client'}.`);
      setLinkingId(null); setLinkClientId('');
      loadContacts();
    } catch { setReviewNotice('Could not link.'); }
    finally { setReviewBusy(null); }
  };

  // accKey: pass the account explicitly when it was just set — `account` state
  // is stale inside this closure on the same tick.
  const openContact = async (c: Contact, accKey?: string) => {
    setView('mail');
    setActive(c);
    setExpanded(null);
    const r = await apiClient.get(`/mail/contacts/${c.id}/messages?account=${accKey ?? account}`).catch(() => null);
    const list: Msg[] = r?.data.data ?? [];
    setMsgs(list);
    if (c.unseen > 0) {
      apiClient.post(`/mail/contacts/${c.id}/seen`).then(() => { loadAccounts(); loadContacts(); }).catch(() => {});
    }
    setTimeout(() => threadEnd.current?.scrollIntoView({ block: 'end' }), 100);
  };

  const toggleMsg = async (m: Msg) => {
    if (expanded === m.id) { setExpanded(null); return; }
    setExpanded(m.id);
    if (!(m.id in openBody)) {
      const r = await apiClient.get(`/mail/messages/${m.id}/body`).catch(() => null);
      const d = r?.data.data;
      const fit = '<style>html,body{max-width:100%;overflow-x:hidden;word-break:break-word;margin:8px;font-family:sans-serif}img,table{max-width:100%!important;height:auto}</style>';
      setOpenBody(prev => ({ ...prev, [m.id]: fit + (d?.body_html || `<pre style="white-space:pre-wrap;font-family:inherit">${(d?.body_text || '(no content)').replace(/</g, '&lt;')}</pre>`) }));
    }
  };

  const toHtml = (text: string) =>
    text.split('\n').map(l => l.trim() ? `<p>${l.replace(/</g, '&lt;')}</p>` : '<br/>').join('\n');

  const send = async (toOverride?: string) => {
    const to = toOverride ?? cTo;
    if (!to.trim() || !cSubject.trim() || !cBody.trim()) return;
    setSending(true);
    setNotice('');
    try {
      const form = new FormData();
      form.append('to', to.trim());
      form.append('subject', cSubject.trim());
      form.append('from', cFrom);
      form.append('bodyHtml', toHtml(cBody));
      if (cCc.trim()) form.append('cc', cCc.trim());
      cFiles.forEach(f => form.append('files', f));
      await apiClient.post('/mail/send', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      setCFiles([]);
      setNotice('Sent ✓ — filed to Sent and this thread.');
      setCSubject(''); setCBody(''); setCTo(''); setCCc(''); setTemplateId(''); setPreviewHtml(null);
      setComposeOpen(false);
      if (active) openContact(active);
      loadAccounts(); loadContacts();
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } };
      setNotice(err.response?.data?.error || 'Send failed.');
    } finally { setSending(false); }
  };

  // #80 — saved templates (shared with Outreach) + true branded preview
  const openCompose = () => {
    setComposeOpen(true);
    setCTo(''); setCSubject(''); setCBody(''); setCCc(''); setTemplateId(''); setPreviewHtml(null);
    setCAiInstructions('');
    if (!templates.length) {
      apiClient.get('/outreach/templates').then(r => setTemplates(r.data.data ?? [])).catch(() => {});
    }
  };

  const applyTemplate = (id: string) => {
    setTemplateId(id);
    setPreviewHtml(null);
    const t = templates.find(x => x.id === id);
    if (!t) return;
    if (t.subject) setCSubject(t.subject);
    setCBody(t.body || '');
  };

  // #90 — save the current compose as a reusable template (shared library
  // with Outreach; it appears in the picker immediately)
  const saveAsTemplate = async () => {
    if (!cBody.trim()) return;
    const title = window.prompt('Template name (e.g. "Quote follow-up")');
    if (!title?.trim()) return;
    const category = window.prompt('Category (e.g. General, Clients, Billing)', 'General') || 'General';
    try {
      const r = await apiClient.post('/outreach/templates', {
        title: title.trim(), category: category.trim() || 'General',
        subject: cSubject.trim(), body: cBody,
      });
      const id = r.data.data?.id as string;
      const t = { id, title: title.trim(), category: category.trim() || 'General', subject: cSubject.trim(), body: cBody };
      setTemplates(prev => [...prev, t]);
      setTemplateId(id);
      setNotice('Saved as template ✓');
      setTimeout(() => setNotice(''), 2000);
    } catch { setNotice('Could not save the template.'); }
  };

  const togglePreview = async () => {
    if (previewHtml) { setPreviewHtml(null); return; }
    setPreviewing(true);
    try {
      const r = await apiClient.post('/mail/preview', { from: cFrom, bodyHtml: toHtml(cBody) });
      setPreviewHtml(r.data.data.html as string);
    } catch { setNotice('Preview unavailable.'); }
    finally { setPreviewing(false); }
  };

  const openAttRow = (a: AttRow) => {
    const token = localStorage.getItem('accessToken');
    fetch(`/api/mail/messages/${a.message_id}/attachments/${a.idx}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { if (!r.ok) throw new Error(); return r.blob(); })
      .then(b => window.open(URL.createObjectURL(b), '_blank'))
      .catch(() => setNotice('Could not open that attachment.'));
  };

  // messageId: draft a reply to that specific message (per-message "AI reply");
  // without it the server uses the contact's recent thread as before.
  const aiDraft = async (messageId?: number) => {
    if (!active) return;
    setDrafting(true);
    try {
      const body: { instructions?: string; message_id?: number } = {};
      if (aiInstructions.trim()) body.instructions = aiInstructions.trim();
      if (messageId != null) body.message_id = messageId;
      const r = await apiClient.post(`/mail/contacts/${active.id}/draft-reply`, body);
      const d = r.data.data as { draft: string; subject: string };
      setCBody(d.draft);
      const next = d.subject || `Re: ${active.last_subject || ''}`.trim();
      if (!cSubject.trim() || cSubject === aiSubjectRef.current) {
        setCSubject(next);
        aiSubjectRef.current = next;
      }
    } catch { setNotice('Could not draft a reply.'); }
    finally { setDrafting(false); }
  };

  // Compose-modal AI draft — no contact; the server matches `to` against
  // known contacts for context. Plain text back, branded template on send.
  const aiCompose = async () => {
    if (!cAiInstructions.trim()) return;
    setCDrafting(true);
    setNotice('');
    try {
      const r = await apiClient.post('/mail/ai-compose', {
        to: cTo.trim() || undefined,
        subject: cSubject.trim() || undefined,
        instructions: cAiInstructions.trim(),
        draft: cBody.trim() ? cBody : undefined,
      });
      if (!composeOpenRef.current) return; // modal closed while drafting
      const d = r.data.data as { draft: string; subject?: string };
      setCBody(d.draft);
      if (!cSubject.trim() && d.subject) setCSubject(d.subject);
      setPreviewHtml(null);
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } };
      setNotice(err.response?.data?.error || 'Could not draft the email.');
    } finally { setCDrafting(false); }
  };

  const makeClient = async () => {
    if (!active || !window.confirm(`Create a client record for ${active.name || active.email}?`)) return;
    const r = await apiClient.post(`/mail/contacts/${active.id}/create-client`).catch(() => null);
    if (r) { setActive({ ...active, client_id: r.data.data.client_id, client_name: active.name || active.email }); loadContacts(); }
  };

  const msgAction = async (m: Msg, action: 'unseen' | 'archive' | 'delete') => {
    if (action === 'delete' && !window.confirm('Remove this message from the app? (The original stays in webmail.)')) return;
    if (action === 'delete') await apiClient.delete(`/mail/messages/${m.id}`).catch(() => {});
    else await apiClient.post(`/mail/messages/${m.id}/${action}`).catch(() => {});
    if (action === 'unseen') { loadAccounts(); loadContacts(); }
    else setMsgs(ms => ms.filter(x => x.id !== m.id));
  };

  const openAttachment = (m: Msg, idx: number) => {
    const token = localStorage.getItem('accessToken');
    fetch(`/api/mail/messages/${m.id}/attachments/${idx}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { if (!r.ok) throw new Error(); return r.blob(); })
      .then(b => window.open(URL.createObjectURL(b), '_blank'))
      .catch(() => setNotice('Could not open that attachment.'));
  };

  const accMeta = (k: string) => accounts.find(a => a.account === k);
  const allUnseen = accounts.reduce((s, a) => s + Number(a.unseen || 0), 0);

  return (
    <MainLayout>
      <div className="flex h-[calc(100vh-8.5rem)] gap-3 min-h-0">
        {/* Accounts rail — level 1: your email addresses */}
        <div className={`w-full lg:w-52 shrink-0 bg-white rounded-xl border border-gray-200 p-2 flex-col gap-0.5 ${level === 0 ? 'flex' : 'hidden'} lg:flex`}>
          <button onClick={openCompose}
            className="mb-2 h-10 rounded-lg bg-[#C77D3F] text-white text-sm font-bold flex items-center justify-center gap-1.5">
            <span className="material-symbols-outlined text-lg">edit</span> Compose
          </button>
          <div className="flex-1 overflow-y-auto min-h-0">
          {ACCOUNTS.map(a => {
            const m = a.key === 'all' ? { unseen: allUnseen } : accMeta(a.key);
            const unseen = Number(m?.unseen || 0);
            const kids = treeContacts[a.key];
            return (
              <div key={a.key}>
                <div className={`flex items-center gap-1 rounded-lg ${account === a.key ? 'bg-[#FAF6F0]' : 'hover:bg-gray-50'}`}>
                  <button onClick={() => toggleTree(a.key)} title="Expand contacts"
                    className="pl-1 py-2 text-gray-300 hover:text-[#C77D3F]">
                    <span className="material-symbols-outlined text-base">{treeOpen.has(a.key) ? 'expand_more' : 'chevron_right'}</span>
                  </button>
                  <button onClick={() => { setView('mail'); setAccount(a.key); setActive(null); setMsgs([]); setLevel(1); }}
                    className={`flex-1 flex items-center gap-1.5 py-2 pr-2 text-sm min-w-0 ${account === a.key ? 'text-[#8a5526] font-bold' : 'text-gray-600'}`}>
                    <span className="material-symbols-outlined text-lg shrink-0">{a.icon}</span>
                    <span className="flex-1 text-left truncate text-[13px]">{a.label}</span>
                    {unseen > 0 && a.key !== 'junk' && (
                      <span className="text-[10px] font-bold bg-[#C77D3F] text-white rounded-full px-1.5 py-0.5 shrink-0">{unseen}</span>
                    )}
                  </button>
                </div>
                {treeOpen.has(a.key) && (
                  <div className="ml-5 border-l border-gray-100 pl-1.5 mb-1">
                    {!kids ? (
                      <p className="text-[11px] text-gray-300 py-1">Loading…</p>
                    ) : kids.length === 0 ? (
                      <p className="text-[11px] text-gray-300 py-1">empty</p>
                    ) : kids.slice(0, 30).map(c => (
                      <button key={c.id}
                        onClick={() => { setAccount(a.key); openContact(c, a.key); setLevel(2); }}
                        className={`w-full flex items-center gap-1 py-1 px-1 rounded text-left hover:bg-gray-50 ${active?.id === c.id ? 'bg-[#FAF6F0]' : ''}`}>
                        <span className={`flex-1 min-w-0 truncate text-[11px] ${Number(c.unseen) > 0 ? 'font-bold text-gray-800' : 'text-gray-500'}`}>
                          {c.name || c.email}
                        </span>
                        <span className="text-[10px] text-gray-400 shrink-0">
                          ({c.msg_count - Number(c.unseen || 0)}/{Number(c.unseen || 0)})
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {/* Contacts root (#81): contact → receiving address → thread */}
          <div className="mt-2 pt-2 border-t border-gray-100">
            <button
              onClick={() => { setContactsRootOpen(o => !o); if (!contactTree) loadContactTree(); }}
              className="w-full flex items-center gap-1.5 py-2 px-1 text-sm text-gray-600 hover:bg-gray-50 rounded-lg">
              <span className="material-symbols-outlined text-base text-gray-300">{contactsRootOpen ? 'expand_more' : 'chevron_right'}</span>
              <span className="material-symbols-outlined text-lg">groups</span>
              <span className="flex-1 text-left font-semibold text-[13px]">Contacts</span>
            </button>
            {contactsRootOpen && (
              <div className="ml-5 border-l border-gray-100 pl-1.5 mb-1">
                {!contactTree ? (
                  <p className="text-[11px] text-gray-300 py-1">Loading…</p>
                ) : contactTree.length === 0 ? (
                  <p className="text-[11px] text-gray-300 py-1">empty</p>
                ) : (() => {
                  const byContact = new Map<number, { name: string; email: string; rows: TreeRow[] }>();
                  for (const row of contactTree) {
                    if (!byContact.has(row.id)) byContact.set(row.id, { name: row.name, email: row.email, rows: [] });
                    byContact.get(row.id)!.rows.push(row);
                  }
                  return [...byContact.entries()].slice(0, 60).map(([cid, node]) => {
                    const totUnseen = node.rows.reduce((s, r) => s + Number(r.unseen || 0), 0);
                    const open = openContactNodes.has(cid);
                    return (
                      <div key={cid}>
                        <button
                          onClick={() => setOpenContactNodes(prev => {
                            const n = new Set(prev);
                            if (n.has(cid)) n.delete(cid); else n.add(cid);
                            return n;
                          })}
                          className="w-full flex items-center gap-1 py-1 px-1 rounded text-left hover:bg-gray-50">
                          <span className="material-symbols-outlined text-sm text-gray-300">{open ? 'expand_more' : 'chevron_right'}</span>
                          <span className={`flex-1 min-w-0 truncate text-[11px] ${totUnseen > 0 ? 'font-bold text-gray-800' : 'text-gray-500'}`}>
                            {node.name || node.email}
                          </span>
                          {totUnseen > 0 && (
                            <span className="text-[9px] font-bold bg-[#C77D3F] text-white rounded-full px-1 shrink-0">{totUnseen}</span>
                          )}
                        </button>
                        {open && node.rows.map(r => (
                          <button key={`${cid}-${r.account}`}
                            onClick={() => {
                              const stub: Contact = {
                                id: cid, email: node.email, name: node.name,
                                msg_count: Number(r.msg_count || 0), unseen: Number(r.unseen || 0),
                                latest: r.latest, last_snippet: '', last_subject: '',
                              };
                              setAccount(r.account); openContact(stub, r.account); setLevel(2);
                            }}
                            className={`w-full flex items-center gap-1 py-0.5 pl-5 pr-1 rounded text-left hover:bg-gray-50 ${active?.id === cid && account === r.account ? 'bg-[#FAF6F0]' : ''}`}>
                            <span className={`flex-1 min-w-0 truncate text-[10px] ${Number(r.unseen) > 0 ? 'font-bold text-gray-700' : 'text-gray-400'}`}>
                              {r.account === 'inbox' ? 'inbox' : `${r.account}@ochrepoint.ca`}
                            </span>
                            <span className="text-[9px] text-gray-400 shrink-0">
                              ({Number(r.msg_count) - Number(r.unseen || 0)}/{Number(r.unseen || 0)})
                            </span>
                          </button>
                        ))}
                      </div>
                    );
                  });
                })()}
              </div>
            )}
          </div>

          {/* Attachments root (#85) */}
          <button
            onClick={() => { setView('attachments'); setLevel(1); if (!attRows) loadAttachments(); }}
            className={`w-full flex items-center gap-1.5 py-2 px-1 text-sm rounded-lg hover:bg-gray-50 ${view === 'attachments' ? 'bg-[#FAF6F0] text-[#8a5526] font-bold' : 'text-gray-600'}`}>
            <span className="material-symbols-outlined text-base text-transparent">chevron_right</span>
            <span className="material-symbols-outlined text-lg">attach_file</span>
            <span className="flex-1 text-left font-semibold text-[13px]">Attachments</span>
          </button>

          {/* Review contacts sweep (#107) */}
          <button
            onClick={() => { setView('review'); setLevel(1); setReviewNotice(''); if (!reviewRows) loadReview(); if (!clientOpts) loadClientOpts(); }}
            className={`w-full flex items-center gap-1.5 py-2 px-1 text-sm rounded-lg hover:bg-gray-50 ${view === 'review' ? 'bg-[#FAF6F0] text-[#8a5526] font-bold' : 'text-gray-600'}`}>
            <span className="material-symbols-outlined text-base text-transparent">chevron_right</span>
            <span className="material-symbols-outlined text-lg">person_search</span>
            <span className="flex-1 text-left font-semibold text-[13px]">Review contacts</span>
            {reviewRows && reviewRows.length > 0 && (
              <span className="text-[10px] font-bold bg-[#C77D3F]/15 text-[#8a5526] rounded-full px-1.5 py-0.5 shrink-0">{reviewRows.length}</span>
            )}
          </button>
          </div>
          <p className="mt-auto text-[10px] text-gray-400 px-2 pb-1">Syncs every 3 min from *@ochrepoint.ca</p>
        </div>

        {view === 'attachments' ? (
          /* Attachments browser (#85): contact → sent / received → files */
          <div className={`flex-1 min-w-0 bg-white rounded-xl border border-gray-200 flex-col min-h-0 overflow-hidden ${level >= 1 ? 'flex' : 'hidden'} lg:flex`}>
            <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-2">
              <button onClick={() => { setView('mail'); setLevel(0); }} className="lg:hidden text-[#8a5526]">
                <span className="material-symbols-outlined text-xl">chevron_left</span>
              </button>
              <span className="material-symbols-outlined text-lg text-[#C77D3F]">attach_file</span>
              <p className="text-sm font-bold text-gray-900">Attachments</p>
              <p className="text-[11px] text-gray-400 ml-auto">
                {attRows ? `${attRows.length} file${attRows.length === 1 ? '' : 's'} · inline signature images hidden` : ''}
              </p>
            </div>
            <div className="flex-1 overflow-y-auto p-2">
              {!attRows ? (
                <p className="p-6 text-center text-sm text-gray-400">Loading attachments…</p>
              ) : attRows.length === 0 ? (
                <p className="p-6 text-center text-sm text-gray-400">No attachments found in your mail.</p>
              ) : (() => {
                const byContact = new Map<number, { name: string; email: string; rows: AttRow[] }>();
                for (const a of attRows) {
                  if (!byContact.has(a.contact_id)) byContact.set(a.contact_id, { name: a.contact_name, email: a.contact_email, rows: [] });
                  byContact.get(a.contact_id)!.rows.push(a);
                }
                const renderRows = (rows: AttRow[]) => (
                  <ul className="divide-y divide-gray-50">
                    {rows.map(a => (
                      <li key={`${a.message_id}-${a.idx}`}>
                        <button onClick={() => openAttRow(a)}
                          className="w-full flex items-center gap-2 px-2 py-1.5 text-left hover:bg-gray-50 rounded">
                          <span className="material-symbols-outlined text-base text-gray-400 shrink-0">draft</span>
                          <span className="flex-1 min-w-0">
                            <span className="block text-xs text-gray-800 truncate">{a.name}</span>
                            <span className="block text-[10px] text-gray-400 truncate">{a.subject || '(no subject)'}</span>
                          </span>
                          <span className="text-[10px] text-gray-400 shrink-0">{fmtSize(Number(a.size || 0))}</span>
                          <span className="text-[10px] text-gray-400 shrink-0 hidden sm:inline">{fmtTime(a.sent_at)}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                );
                return [...byContact.entries()].map(([cid, node]) => {
                  const open = openAttNodes.has(cid);
                  const sent = node.rows.filter(a => a.direction === 'out');
                  const received = node.rows.filter(a => a.direction === 'in');
                  return (
                    <div key={cid} className="mb-1">
                      <button
                        onClick={() => setOpenAttNodes(prev => {
                          const n = new Set(prev);
                          if (n.has(cid)) n.delete(cid); else n.add(cid);
                          return n;
                        })}
                        className="w-full flex items-center gap-1.5 px-2 py-2 rounded-lg text-left hover:bg-gray-50">
                        <span className="material-symbols-outlined text-base text-gray-300">{open ? 'expand_more' : 'chevron_right'}</span>
                        <span className="flex-1 min-w-0 truncate text-sm font-medium text-gray-800">{node.name || node.email}</span>
                        <span className="text-[10px] text-gray-400 shrink-0">{node.rows.length} file{node.rows.length === 1 ? '' : 's'}</span>
                      </button>
                      {open && (
                        <div className="ml-6 mb-2 space-y-1.5">
                          {received.length > 0 && (
                            <div>
                              <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400 px-2 pt-1">Received</p>
                              {renderRows(received)}
                            </div>
                          )}
                          {sent.length > 0 && (
                            <div>
                              <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400 px-2 pt-1">Sent</p>
                              {renderRows(sent)}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                });
              })()}
            </div>
          </div>
        ) : view === 'review' ? (
          /* Review contacts (#107): frequent correspondents with no client link.
             Read-only against mail — the only writes are the explicit actions. */
          <div className={`flex-1 min-w-0 bg-white rounded-xl border border-gray-200 flex-col min-h-0 overflow-hidden ${level >= 1 ? 'flex' : 'hidden'} lg:flex`}>
            <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-2">
              <button onClick={() => { setView('mail'); setLevel(0); }} className="lg:hidden text-[#8a5526]">
                <span className="material-symbols-outlined text-xl">chevron_left</span>
              </button>
              <span className="material-symbols-outlined text-lg text-[#C77D3F]">person_search</span>
              <p className="text-sm font-bold text-gray-900">Review contacts</p>
              <p className="text-[11px] text-gray-400 ml-auto">
                {reviewRows ? `${reviewRows.length} frequent correspondent${reviewRows.length === 1 ? '' : 's'} not linked to a client` : ''}
              </p>
            </div>
            {reviewNotice && <p className="px-4 py-2 text-xs text-green-700 bg-green-50 border-b border-green-100">{reviewNotice}</p>}
            <div className="flex-1 overflow-y-auto">
              {!reviewRows ? (
                <p className="p-6 text-center text-sm text-gray-400">Checking your mail for unlinked correspondents…</p>
              ) : reviewRows.length === 0 ? (
                <p className="p-6 text-center text-sm text-gray-400">All caught up — every frequent correspondent is linked, dismissed, or filtered as noise.</p>
              ) : (
                <ul className="divide-y divide-gray-50">
                  {reviewRows.map(row => (
                    <li key={row.id} className="px-4 py-2.5">
                      <div className="flex flex-wrap items-center gap-2">
                        <div className="flex-1 min-w-[12rem]">
                          <p className="text-sm font-medium text-gray-900 truncate">{row.name || row.email}</p>
                          <p className="text-[11px] text-gray-400 truncate">{row.email}</p>
                        </div>
                        <span className="text-[11px] text-gray-500 shrink-0 whitespace-nowrap">
                          {row.msg_count} msg{Number(row.msg_count) === 1 ? '' : 's'} · last {fmtTime(row.latest)}
                        </span>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <button onClick={() => reviewMakeClient(row)} disabled={reviewBusy === row.id}
                            className="h-7 px-2 rounded-lg border border-gray-200 text-[11px] font-bold text-gray-600 hover:border-[#C77D3F] hover:text-[#C77D3F] disabled:opacity-40 flex items-center gap-1">
                            <span className="material-symbols-outlined text-sm">person_add</span> Make client
                          </button>
                          <button onClick={() => { setLinkingId(l => l === row.id ? null : row.id); setLinkClientId(''); }}
                            disabled={reviewBusy === row.id}
                            className="h-7 px-2 rounded-lg border border-gray-200 text-[11px] font-bold text-gray-600 hover:border-[#C77D3F] hover:text-[#C77D3F] disabled:opacity-40 flex items-center gap-1">
                            <span className="material-symbols-outlined text-sm">link</span> Link…
                          </button>
                          <button onClick={() => reviewDismiss(row)} disabled={reviewBusy === row.id}
                            title="Hide this address from the sweep permanently"
                            className="h-7 px-2 rounded-lg text-[11px] font-semibold text-gray-400 hover:text-gray-600 disabled:opacity-40">
                            Dismiss
                          </button>
                        </div>
                      </div>
                      {linkingId === row.id && (
                        <div className="mt-2 flex items-center gap-2">
                          <select value={linkClientId} onChange={e => setLinkClientId(e.target.value)}
                            className="flex-1 max-w-xs border border-gray-200 rounded-lg px-2 py-1.5 text-xs bg-white">
                            <option value="">Link {row.email} to…</option>
                            {(clientOpts ?? []).map(c => (
                              <option key={c.id} value={c.id}>{c.name}{c.email ? ` (${c.email})` : ' (no email)'}</option>
                            ))}
                          </select>
                          <button onClick={() => reviewLink(row)} disabled={!linkClientId || reviewBusy === row.id}
                            className="h-7 px-3 rounded-lg bg-[#C77D3F] text-white text-[11px] font-bold disabled:opacity-40">
                            Link
                          </button>
                          <p className="text-[10px] text-gray-400 hidden sm:block">Fills the client's email if empty; otherwise the address goes to their notes.</p>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        ) : (
        <>
        {/* Senders — level 2: who mailed this address */}
        <div className={`w-full lg:w-80 shrink-0 bg-white rounded-xl border border-gray-200 flex-col min-h-0 ${level === 1 ? 'flex' : 'hidden'} lg:flex`}>
          <div className="px-2 pt-2 lg:hidden">
            <button onClick={() => setLevel(0)} className="flex items-center gap-1 text-xs font-semibold text-[#8a5526] mb-1">
              <span className="material-symbols-outlined text-base">chevron_left</span>
              {ACCOUNTS.find(a => a.key === account)?.label}
            </button>
          </div>
          <div className="p-2 border-b border-gray-100">
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search people, subjects, message text…"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C77D3F]/30" />
          </div>
          <div className="flex-1 overflow-y-auto divide-y divide-gray-50">
            {contacts.length === 0 ? (
              <p className="p-6 text-center text-sm text-gray-400">No conversations here.</p>
            ) : contacts.map(c => (
              <button key={c.id} onClick={() => { openContact(c); setLevel(2); }}
                className={`w-full text-left px-3 py-2.5 hover:bg-gray-50 ${active?.id === c.id ? 'bg-[#FAF6F0]' : ''}`}>
                <div className="flex items-baseline justify-between gap-2">
                  <span className={`text-sm truncate ${Number(c.unseen) > 0 ? 'font-bold text-gray-900' : 'font-medium text-gray-700'}`}>
                    {c.name || c.email}
                  </span>
                  <span className="text-[10px] text-gray-400 shrink-0">{fmtTime(c.latest)}</span>
                </div>
                <p className="text-xs text-gray-500 truncate">{c.last_subject}</p>
                <p className="text-[11px] text-gray-400 truncate">{c.last_snippet}</p>
                {Number(c.unseen) > 0 && (
                  <span className="inline-block mt-1 text-[10px] font-bold bg-[#C77D3F]/15 text-[#8a5526] rounded-full px-1.5 py-0.5">{c.unseen} new</span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Messages — level 3: mail from this sender */}
        <div className={`flex-1 min-w-0 max-w-full overflow-hidden bg-white rounded-xl border border-gray-200 flex-col min-h-0 ${level === 2 ? 'flex' : 'hidden'} lg:flex`}>
          {!active ? (
            <div className="flex-1 flex flex-col items-center justify-center text-gray-300">
              <span className="material-symbols-outlined text-6xl mb-2">mark_email_unread</span>
              <p className="text-sm text-gray-400">Pick a conversation — every contact is auto-created from your real mail.</p>
            </div>
          ) : (
            <>
              <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-2">
                <button onClick={() => setLevel(1)} className="lg:hidden text-[#8a5526]">
                  <span className="material-symbols-outlined text-xl">chevron_left</span>
                </button>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-gray-900 truncate">{active.name || active.email}</p>
                  <p className="text-[11px] text-gray-400 truncate">
                    {account !== 'all' ? `${ACCOUNTS.find(a => a.key === account)?.label} › ` : ''}{active.email} · {active.msg_count} message{active.msg_count === 1 ? '' : 's'}
                  </p>
                </div>
                {active.client_id ? (
                  <a href={`/clients/${active.client_id}`}
                    className="shrink-0 flex items-center gap-1 text-[11px] font-bold bg-green-100 text-green-800 rounded-full px-2 py-1 hover:bg-green-200">
                    <span className="material-symbols-outlined text-sm">verified</span>
                    {active.client_name || 'Client'}
                  </a>
                ) : (
                  <button onClick={makeClient}
                    className="shrink-0 flex items-center gap-1 text-[11px] font-bold border border-gray-200 text-gray-500 rounded-full px-2 py-1 hover:border-[#C77D3F] hover:text-[#C77D3F]">
                    <span className="material-symbols-outlined text-sm">person_add</span> Make client
                  </button>
                )}
              </div>
              <div className="flex-1 overflow-y-auto overflow-x-hidden p-3 space-y-2">
                {msgs.map(m => (
                  <div key={m.id} className={`max-w-[85%] min-w-0 ${m.direction === 'out' ? 'ml-auto' : ''}`}>
                    <button onClick={() => toggleMsg(m)}
                      className={`w-full text-left rounded-xl border px-3 py-2 ${m.direction === 'out' ? 'bg-[#FAF6F0] border-[#C77D3F]/25' : 'bg-white border-gray-200'}`}>
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="text-xs font-bold text-gray-700 truncate">
                          {m.direction === 'out' ? `You (${m.from_addr.split('@')[0]})` : (m.from_name || m.from_addr)}
                        </span>
                        <span className="text-[10px] text-gray-400 shrink-0">
                          {new Date(m.sent_at).toLocaleString('en-CA', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                        </span>
                      </div>
                      <p className="text-sm text-gray-900 break-words">
                        {(m.lead_score ?? 0) >= 60 && (
                          <span className="inline-flex items-center gap-0.5 mr-1.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800 align-middle"
                            title={m.lead_reason || 'Flagged as a possible work lead'}>
                            🔨 LEAD
                          </span>
                        )}
                        {m.subject}
                      </p>
                      {expanded !== m.id && <p className="text-xs text-gray-400 truncate">{m.snippet}</p>}
                      {m.attachments_json && (
                        <span className="flex flex-wrap gap-1 mt-0.5">
                          {(JSON.parse(m.attachments_json) as { name: string }[]).map((a, i) => (
                            <span key={i} role="button" tabIndex={0}
                              onClick={e => { e.stopPropagation(); openAttachment(m, i); }}
                              className="text-[11px] text-[#8a5526] underline decoration-dotted hover:decoration-solid">
                              📎 {a.name}
                            </span>
                          ))}
                        </span>
                      )}
                    </button>
                    {expanded === m.id && (
                      <div className="flex justify-end gap-1 mt-1">
                        <button onClick={() => aiDraft(m.id)} disabled={drafting} title="AI reply to this message — drafts into the reply box"
                          className="h-6 px-1.5 rounded text-gray-400 hover:text-[#C77D3F] disabled:opacity-40"><span className={`material-symbols-outlined text-base ${drafting ? 'animate-spin' : ''}`}>{drafting ? 'progress_activity' : 'auto_awesome'}</span></button>
                        <button onClick={() => msgAction(m, 'unseen')} title="Mark unread"
                          className="h-6 px-1.5 rounded text-gray-400 hover:text-[#C77D3F]"><span className="material-symbols-outlined text-base">mark_email_unread</span></button>
                        <button onClick={() => msgAction(m, 'archive')} title="Archive (hide from app)"
                          className="h-6 px-1.5 rounded text-gray-400 hover:text-[#C77D3F]"><span className="material-symbols-outlined text-base">archive</span></button>
                        <button onClick={() => msgAction(m, 'delete')} title="Remove from app"
                          className="h-6 px-1.5 rounded text-gray-400 hover:text-red-600"><span className="material-symbols-outlined text-base">delete</span></button>
                      </div>
                    )}
                    {expanded === m.id && (
                      <div className="mt-1 rounded-xl border border-gray-200 bg-white overflow-hidden">
                        <iframe title={`msg-${m.id}`} sandbox="" srcDoc={openBody[m.id] || '<p style="color:#999;font-family:sans-serif">Loading…</p>'}
                          className="w-full bg-white" style={{ height: '24rem' }} />
                      </div>
                    )}
                  </div>
                ))}
                <div ref={threadEnd} />
              </div>
              {/* Reply box */}
              <div className="border-t border-gray-100 p-3 space-y-2">
                {notice && <p className="text-xs text-green-700">{notice}</p>}
                <div className="flex gap-2">
                  <select value={cFrom} onChange={e => setCFrom(e.target.value)}
                    className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs bg-white">
                    {senders.map(s => <option key={s.key} value={s.key}>{s.email}</option>)}
                  </select>
                  <input value={cSubject} onChange={e => setCSubject(e.target.value)} placeholder="Subject"
                    className="flex-1 border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none" />
                </div>
                <div className="flex gap-2 items-end">
                  <textarea value={cBody} onChange={e => setCBody(e.target.value)} rows={2}
                    placeholder={`Reply to ${active.name || active.email} — sent with your branded template`}
                    className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none" />
                  <input value={aiInstructions} onChange={e => setAiInstructions(e.target.value)}
                    placeholder="Optional: tell the AI what to say" title="Optional guidance for the AI draft"
                    className="w-36 sm:w-52 h-10 border border-gray-200 rounded-lg px-2.5 text-xs focus:outline-none focus:ring-2 focus:ring-[#C77D3F]/30" />
                  <button onClick={() => aiDraft()} disabled={drafting} title="Read the thread and draft a reply in your voice"
                    className="h-10 px-2.5 rounded-lg border border-[#C77D3F]/40 text-[#8a5526] text-xs font-bold disabled:opacity-40 flex items-center gap-1">
                    <span className={`material-symbols-outlined text-base ${drafting ? 'animate-spin' : ''}`}>{drafting ? 'progress_activity' : 'auto_awesome'}</span>
                    {drafting ? '' : 'AI draft'}
                  </button>
                  <button onClick={() => send(active.email)} disabled={sending || !cSubject.trim() || !cBody.trim()}
                    className="h-10 px-4 rounded-lg bg-[#C77D3F] text-white text-sm font-bold disabled:opacity-40">
                    {sending ? 'Sending…' : 'Send'}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
        </>
        )}
      </div>

      {/* New-message modal */}
      {composeOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setComposeOpen(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto p-5 space-y-3" onClick={e => e.stopPropagation()}>
            <h3 className="font-bold text-gray-900 flex items-center gap-1.5">
              <span className="material-symbols-outlined text-[#C77D3F]">edit</span> New email
            </h3>
            <div className="flex flex-wrap gap-2">
              <select value={cFrom} onChange={e => { setCFrom(e.target.value); setPreviewHtml(null); }}
                className="border border-gray-300 rounded-lg px-2 py-2 text-sm bg-white">
                {senders.map(s => <option key={s.key} value={s.key}>{s.email}</option>)}
              </select>
              {/* Saved template picker (#80) — same library as Outreach */}
              <select value={templateId} onChange={e => applyTemplate(e.target.value)}
                className="flex-1 min-w-[10rem] border border-gray-300 rounded-lg px-2 py-2 text-sm bg-white text-gray-600">
                <option value="">Template: none (blank)</option>
                {[...new Set(templates.map(t => t.category))].map(cat => (
                  <optgroup key={cat} label={cat}>
                    {templates.filter(t => t.category === cat).map(t => (
                      <option key={t.id} value={t.id}>{t.title}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>
            <input value={cTo} onChange={e => setCTo(e.target.value)} placeholder="To"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none" />
            <input value={cCc} onChange={e => setCCc(e.target.value)} placeholder="Cc (optional)"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none" />
            <input value={cSubject} onChange={e => setCSubject(e.target.value)} placeholder="Subject"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none" />
            {previewHtml ? (
              <div className="border border-[#C77D3F]/30 rounded-lg overflow-hidden">
                <p className="text-[10px] font-bold uppercase tracking-wide text-[#8a5526] bg-[#FAF6F0] px-3 py-1">Exactly what the recipient sees</p>
                <iframe title="compose-preview" sandbox="" srcDoc={previewHtml} className="w-full bg-white" style={{ height: '20rem' }} />
              </div>
            ) : (
              <textarea value={cBody} onChange={e => { setCBody(e.target.value); }} rows={9}
                placeholder="Message — wrapped in your branded compose template on send"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-y focus:outline-none" />
            )}
            <div className="flex flex-wrap items-center gap-3">
              <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 cursor-pointer">
                <span className="material-symbols-outlined text-base">attach_file</span>
                {cFiles.length ? cFiles.map(f => f.name).join(', ') : 'Attach files'}
                <input type="file" multiple className="sr-only"
                  onChange={e => setCFiles([...(e.target.files || [])])} />
              </label>
              <input value={cAiInstructions} onChange={e => setCAiInstructions(e.target.value)}
                placeholder="Tell the AI what this email should say"
                className="flex-1 min-w-[10rem] h-8 border border-gray-200 rounded-lg px-2.5 text-xs focus:outline-none focus:ring-2 focus:ring-[#C77D3F]/30" />
              <button onClick={aiCompose} disabled={cDrafting || !cAiInstructions.trim()}
                title="Draft this email with AI — refines the current text if any"
                className="flex items-center gap-1 text-xs font-bold text-[#8a5526] border border-[#C77D3F]/40 rounded-lg px-2.5 h-8 disabled:opacity-40">
                <span className={`material-symbols-outlined text-base ${cDrafting ? 'animate-spin' : ''}`}>
                  {cDrafting ? 'progress_activity' : 'auto_awesome'}
                </span>
                AI draft
              </button>
              <button onClick={saveAsTemplate} disabled={!cBody.trim()}
                title="Save this email as a reusable template (name + category)"
                className="ml-auto flex items-center gap-1 text-xs font-bold text-gray-500 border border-gray-200 rounded-lg px-2.5 h-8 disabled:opacity-40 hover:border-[#C77D3F]/40 hover:text-[#8a5526]">
                <span className="material-symbols-outlined text-base">bookmark_add</span>
                Save as template
              </button>
              <button onClick={togglePreview} disabled={previewing || (!cBody.trim() && !previewHtml)}
                className="flex items-center gap-1 text-xs font-bold text-[#8a5526] border border-[#C77D3F]/40 rounded-lg px-2.5 h-8 disabled:opacity-40">
                <span className={`material-symbols-outlined text-base ${previewing ? 'animate-spin' : ''}`}>
                  {previewing ? 'progress_activity' : previewHtml ? 'edit' : 'visibility'}
                </span>
                {previewHtml ? 'Back to editing' : 'Preview'}
              </button>
            </div>
            {notice && <p className="text-xs text-red-600">{notice}</p>}
            <div className="flex gap-2 justify-end">
              <button onClick={() => setComposeOpen(false)} className="h-10 px-4 rounded-lg border border-gray-300 text-sm font-semibold text-gray-600">Cancel</button>
              <button onClick={() => send()} disabled={sending || !cTo.trim() || !cSubject.trim() || !cBody.trim()}
                className="h-10 px-5 rounded-lg bg-[#C77D3F] text-white text-sm font-bold disabled:opacity-40">
                {sending ? 'Sending…' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
};
